import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { normalizeError } from "@/lib/normalizeError";

export type IdeaStatus = "saved" | "in_progress" | "shipped";

export interface FavoriteWithStatus {
  id: string;
  idea_id: string;
  created_at: string;
  status: IdeaStatus;
  project_id?: string;
  ideas?: {
    id: string;
    title: string;
    tagline?: string | null;
    description: string | null;
    tags: string[] | null;
    niche: string;
    updated_at: string;
    score: number;
  };
}

export const useFavorites = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: favorites, isLoading } = useQuery({
    queryKey: ["favorites", user?.id],
    queryFn: async (): Promise<FavoriteWithStatus[]> => {
      if (!user) return [];

      // Single query with embedded join — avoids RLS issues on separate ideas query
      const { data: favRows, error: favErr } = await supabase
        .from("favorites")
        .select("id, idea_id, created_at, ideas:idea_id(id, title, tagline, description, tags, niche, updated_at, score)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (favErr) {
        console.error("[useFavorites] query error:", favErr.message, favErr.code, favErr.details);
        // Fallback: try without join in case the relationship isn't set up
        const { data: plainFavs, error: plainErr } = await supabase
          .from("favorites")
          .select("id, idea_id, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (plainErr) throw plainErr;
        if (!plainFavs || plainFavs.length === 0) return [];

        // Fetch ideas separately as fallback
        const ideaIds = plainFavs.map((f) => f.idea_id);
        const { data: ideas } = await supabase
          .from("ideas")
          .select("id, title, tagline, description, tags, niche, updated_at, score")
          .in("id", ideaIds);

        const ideaMap = new Map(ideas?.map((i) => [i.id, i]) || []);
        const { data: projects } = await supabase
          .from("code_projects")
          .select("id, idea_id, project_status, build_step")
          .in("idea_id", ideaIds);
        const projectMap = new Map(projects?.map((p) => [p.idea_id, p]) || []);

        return plainFavs.map((f) => {
          const project = projectMap.get(f.idea_id);
          let status: IdeaStatus = "saved";
          if (project) {
            status = project.project_status === "deployed" ||
              ["deploy", "deployed", "live"].includes(project.build_step?.toLowerCase?.() || "")
              ? "shipped" : "in_progress";
          }
          return { ...f, status, project_id: project?.id, ideas: ideaMap.get(f.idea_id) };
        });
      }

      if (!favRows || favRows.length === 0) return [];

      // Fetch project status for saved ideas
      const ideaIds = favRows.map((f) => f.idea_id);
      const { data: projects } = await supabase
        .from("code_projects")
        .select("id, idea_id, project_status, build_step")
        .in("idea_id", ideaIds);

      const projectMap = new Map(projects?.map((p) => [p.idea_id, p]) || []);

      return favRows.map((f) => {
        const project = projectMap.get(f.idea_id);
        let status: IdeaStatus = "saved";

        if (project) {
          status =
            project.project_status === "deployed" ||
            ["deploy", "deployed", "live"].includes(
              project.build_step?.toLowerCase?.() || ""
            )
              ? "shipped"
              : "in_progress";
        }

        // Handle both joined data shape (object) and null
        const ideaData = f.ideas as any;
        const ideas = ideaData && typeof ideaData === "object" && ideaData.id
          ? ideaData
          : undefined;

        return {
          id: f.id,
          idea_id: f.idea_id,
          created_at: f.created_at,
          status,
          project_id: project?.id,
          ideas,
        };
      });
    },
    enabled: !!user,
  });

  const toggleFavorite = useMutation({
    mutationFn: async ({
      ideaId,
      isFavorited,
    }: {
      ideaId: string;
      isFavorited: boolean;
    }) => {
      if (!user) throw new Error("Not authenticated");

      if (isFavorited) {
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("idea_id", ideaId);

        if (error) throw error;
        return { action: "removed" as const };
      } else {
        const { error } = await supabase
          .from("favorites")
          .insert({ user_id: user.id, idea_id: ideaId });

        if (error) throw error;
        return { action: "added" as const };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["is-favorited"] });
      // Also invalidate the StartupExplorer "saved" tab query
      queryClient.invalidateQueries({ queryKey: ["ideas", "saved"] });

      toast({
        title:
          data.action === "added"
            ? "Added to favorites"
            : "Removed from favorites",
      });
    },
    onError: (err) => {
      const e = normalizeError(err);
      toast({
        title: "Error",
        description: e.message,
        variant: "destructive",
      });
    },
  });

  const savedIdeas = favorites || [];
  const inProgressIdeas = favorites?.filter((f) => f.status === "in_progress") || [];
  const shippedIdeas = favorites?.filter((f) => f.status === "shipped") || [];

  return {
    favorites,
    savedIdeas,
    inProgressIdeas,
    shippedIdeas,
    isLoading,
    toggleFavorite: toggleFavorite.mutate,
    isToggling: toggleFavorite.isPending,
  };
};

export const useIsFavorited = (ideaId: string) => {
  const { user } = useAuth();

  const { data: isFavorited, isLoading } = useQuery({
    queryKey: ["is-favorited", ideaId, user?.id],
    queryFn: async () => {
      if (!user) return false;

      const { data } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("idea_id", ideaId)
        .maybeSingle();

      return !!data;
    },
    enabled: !!user,
  });

  return { isFavorited: isFavorited || false, isLoading };
};
